// src/components/OrganizationManagement/ConfirmationDialog.js
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './ConfirmationDialog.css';

/**
 * Reusable confirmation dialog with optional type-to-confirm pattern
 * 
 * Used for destructive actions like organization deletion and deauthorization
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the dialog is visible
 * @param {string} props.title - Dialog title (e.g., "Confirm Deletion")
 * @param {string} props.message - Main confirmation message
 * @param {string} props.warningMessage - Optional warning message with ⚠️ icon
 * @param {string} props.noteMessage - Optional note message
 * @param {string} props.confirmText - Text for confirm button (e.g., "Delete")
 * @param {string} props.confirmationTarget - Text user must type to enable confirm button
 * @param {boolean} props.requiresTyping - Whether type-to-confirm is required
 * @param {boolean} props.disabled - Whether confirm button should be disabled
 * @param {boolean} props.loading - Whether an action is in progress
 * @param {Function} props.onConfirm - Callback when confirmed
 * @param {Function} props.onCancel - Callback when cancelled
 * @param {Function} props.renderExtraContent - Optional function to render extra content
 */
const ConfirmationDialog = ({
  isOpen,
  title,
  message,
  warningMessage,
  noteMessage,
  confirmText = 'Confirm',
  confirmationTarget,
  requiresTyping = false,
  disabled = false,
  loading = false,
  onConfirm,
  onCancel,
  renderExtraContent,
}) => {
  const [typedValue, setTypedValue] = useState('');

  if (!isOpen) return null;

  const handleClose = () => {
    setTypedValue('');
    onCancel();
  };

  const handleConfirm = () => {
    setTypedValue('');
    onConfirm();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !isConfirmDisabled) {
      handleConfirm();
    }
  };

  const isConfirmDisabled =
    loading ||
    disabled ||
    (requiresTyping && typedValue.trim() !== confirmationTarget);

  return (
    <div className="confirmation-dialog-overlay" onClick={handleClose}>
      <div
        className="confirmation-dialog-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="confirmation-dialog-header">
          <h3>{title}</h3>
        </div>

        <div className="confirmation-dialog-body">
          <p>{message}</p>

          {renderExtraContent && renderExtraContent()}

          {warningMessage && (
            <div className="confirmation-dialog-warning">
              ⚠️ {warningMessage}
            </div>
          )}

          {noteMessage && (
            <p className="confirmation-dialog-note">{noteMessage}</p>
          )}

          {requiresTyping && confirmationTarget && (
            <div className="typed-confirmation-section">
              <p className="typed-confirmation-instruction">
                Type <strong>{confirmationTarget}</strong> to confirm:
              </p>
              <input
                type="text"
                className="typed-confirmation-input"
                value={typedValue}
                onChange={(e) => setTypedValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={`Type "${confirmationTarget}"`}
                autoFocus
                disabled={loading}
              />
            </div>
          )}
        </div>

        <div className="confirmation-dialog-actions">
          <button
            className="cancel-button"
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="confirm-button"
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

ConfirmationDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.node.isRequired,
  warningMessage: PropTypes.string,
  noteMessage: PropTypes.string,
  confirmText: PropTypes.string,
  confirmationTarget: PropTypes.string,
  requiresTyping: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  renderExtraContent: PropTypes.func,
};

export default ConfirmationDialog;
